import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function readStored(storageKey, defaults) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ...defaults };
    }
    return mergeWithDefaults(defaults, parsed);
  } catch {
    return { ...defaults };
  }
}

function mergeWithDefaults(defaults, stored) {
  const next = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (!(key in stored)) continue;
    const defVal = defaults[key];
    const storedVal = stored[key];
    if (
      defVal !== null &&
      typeof defVal === 'object' &&
      !Array.isArray(defVal) &&
      storedVal !== null &&
      typeof storedVal === 'object' &&
      !Array.isArray(storedVal)
    ) {
      next[key] = { ...defVal, ...storedVal };
    } else if (typeof storedVal === typeof defVal || defVal === null || defVal === undefined) {
      next[key] = storedVal;
    }
  }
  return next;
}

function isActiveFilterValue(value, defaultValue) {
  if (value === defaultValue) return false;
  if (value == null || value === '') return false;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(defaultValue || {}).some(
      (k) => isActiveFilterValue(value[k], (defaultValue || {})[k])
    );
  }
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Persist list filter state in localStorage until the user clears it.
 *
 * @param {string} storageKey e.g. 'wm_orders_list_filters'
 * @param {object} defaults initial filter bag (also used by clear())
 * @param {{ debounceMs?: number }} [options]
 * @returns {{ values: object, setValues: Function, setField: Function, clear: Function, hasActiveFilters: boolean }}
 */
export default function usePersistedListFilters(storageKey, defaults, options = {}) {
  const { debounceMs = 200 } = options;
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const [values, setValuesState] = useState(() => readStored(storageKey, defaults));
  const skipPersistRef = useRef(false);
  const timerRef = useRef(null);

  const writeStorage = useCallback(
    (next) => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // ignore quota / private mode
      }
    },
    [storageKey]
  );

  useEffect(() => {
    if (skipPersistRef.current) {
      skipPersistRef.current = false;
      return undefined;
    }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      writeStorage(values);
      timerRef.current = null;
    }, debounceMs);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [values, writeStorage, debounceMs]);

  const setValues = useCallback((updater) => {
    setValuesState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const setField = useCallback((key, valueOrUpdater) => {
    setValuesState((prev) => {
      const nextVal =
        typeof valueOrUpdater === 'function' ? valueOrUpdater(prev[key]) : valueOrUpdater;
      return { ...prev, [key]: nextVal };
    });
  }, []);

  const clear = useCallback(() => {
    skipPersistRef.current = true;
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const reset = { ...defaultsRef.current };
    // Deep-clone nested default objects so later mutations don't share refs
    for (const key of Object.keys(reset)) {
      const v = reset[key];
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        reset[key] = { ...v };
      }
    }
    setValuesState(reset);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [storageKey]);

  const hasActiveFilters = useMemo(() => {
    const defs = defaultsRef.current;
    return Object.keys(defs).some((key) => isActiveFilterValue(values[key], defs[key]));
  }, [values]);

  return { values, setValues, setField, clear, hasActiveFilters };
}
